import LaravelError from '@/api/model/LaravelError';
import { useAuthStore } from '@/stores/authStore';
import type { RouteLocationGeneric, RouteLocationNormalized } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import NProgress from 'nprogress';

// Main layout variations
const LayoutBackend = () => import('@/layouts/variations/Backend.vue');
const LayoutBackendFull = () => import('@/layouts/variations/BackendFull.vue');
const LayoutSimple = () => import('@/layouts/variations/Simple.vue');

const AdvertisingPage = () => import('@/views/advertising/AdvertisingPage.vue');
const Dashboard = () => import('@/views/dashboard/DashboardPage.vue');
const MapSalesView = () => import('@/views/dashboard/MapSalesView.vue');

const DisputesList = () => import('@/views/disputes/DisputesList.vue');

const ForecastsList = () => import('@/views/forecasts/ForecastsList.vue');

const FulfillmentCenterList = () => import('@/views/fulfillment_centers/FulfillmentCenterList.vue');
const FulfillmentCenterEdit = () => import('@/views/fulfillment_centers/FulfillmentCenterEdit.vue');

const HealthPage = () => import('@/views/health/HealthPage.vue');

const InventoryByDate = () => import('@/views/inventory/InventoryByDate.vue');

const ShipmentsView = () => import('@/views/shipments/ShipmentsView.vue');

const ProductList = () => import('@/views/products/ProductList.vue');

const PurchaseOrderList = () => import('@/views/purchase_orders/PurchaseOrderList.vue');

const ReportRequestList = () => import('@/views/report_requests/ReportRequestList.vue');
const ReportRequestStatus = () => import('@/views/report_requests/ReportRequestStatus.vue');

const IntegrationsCreate = () => import('@/views/integrations/IntegrationsCreate.vue');
const IntegrationsEdit = () => import('@/views/integrations/IntegrationsEdit.vue');

const SalesChannelsList = () => import('@/views/sales_channels/SalesChannelList.vue');
const SalesChannelsEdit = () => import('@/views/sales_channels/SalesChannelEdit.vue');

const StoreOrderList = () => import('@/views/store_orders/StoreOrderList.vue');
const SubscriptionsList = () => import('@/views/subscriptions/SubscriptionsList.vue');
const CustomersList = () => import('@/views/customers/CustomersList.vue');
const CustomerSupportDashboard = () => import('@/views/customer-support/CustomerSupportDashboard.vue');

const WarehouseTransfersList = () => import('@/views/warehouse_transfers/WarehouseTransfersList.vue');

const SkuMapping = () => import('@/views/skus/SkuMappings.vue');
const SkuList = () => import('@/views/skus/SkuList.vue');
const SkuShow = () => import('@/views/skus/SkuShow.vue');

const Login = () => import('@/views/auth/Login.vue');
const Register = () => import('@/views/auth/Register.vue');
const ForgotPassword = () => import('@/components/auth/passwords/ForgotPassword.vue');
const ResetPassword = () => import('@/components/auth/passwords/ResetPassword.vue');
const CreatePassword = () => import('@/components/auth/passwords/CreatePassword.vue');
const StockAnalysis = () => import('@/views/analysis/StockAnalysis.vue');
const StockStatus = () => import('@/views/stocks/StockStatus.vue');

const SuppliersList = () => import('@/views/suppliers/SuppliersList.vue');

const WarehouseList = () => import('@/views/warehouses/WarehouseList.vue');

const AmazonOAuthForm = () => import('@/views/amazon/oauth/AmazonOAuthForm.vue');

const UserProfile = () => import('@/views/user/UserProfile.vue');

const OrganisationSettings = () => import('@/views/organisation/OrganisationSettings.vue');

const VATReports = () => import('@/views/accounting/VATReports.vue');

const CrossBorderVATReport = () => import('@/views/accounting/CrossBorderVATReport.vue');
const ProfitLossPage = () => import('@/views/profit-loss/ProfitLossPage.vue');

const TrendsView = () => import('@/views/dashboard/TrendsView.vue');
const InventoryRoutingPage = () => import('@/views/stores/InventoryRoutingPage.vue');
const StoresList = () => import('@/views/stores/StoresList.vue');

const CouponsList = () => import('@/views/marketing/CouponsList.vue');

const routes = [
    {
        path: '/organisation/:organisation',
        component: LayoutBackend,
        meta: { requiresAuth: true },
        children: [
            { path: '', name: 'dashboard', component: Dashboard, meta: { helpKey: 'dashboard' } },
            { path: 'map', name: 'dashboard.map', component: MapSalesView, meta: { helpKey: 'dashboard.map' } },
            { path: 'trends', name: 'dashboard.trends', component: TrendsView, meta: { helpKey: 'dashboard.trends' } },

            { path: 'advertising', name: 'advertising', component: AdvertisingPage, meta: { helpKey: 'advertising' } },

            { path: 'disputes', name: 'disputes', component: DisputesList, meta: { helpKey: 'disputes' } },

            { path: 'forecasts', name: 'forecasts', component: ForecastsList, meta: { helpKey: 'forecasts' } },
            {
                path: 'fulfillment-centers',
                name: 'fulfillment-centers',
                component: FulfillmentCenterList,
                meta: { helpKey: 'fulfillment-centers' },
            },
            {
                path: 'fulfillment-centers/:publicId/edit',
                name: 'fulfillment-centers.edit',
                component: FulfillmentCenterEdit,
                props: getPublicId,
                meta: { helpKey: 'fulfillment-centers.edit' },
            },

            {
                path: 'inventory/by-date',
                name: 'inventory-by-date',
                component: InventoryByDate,
                meta: { helpKey: 'inventory-by-date' },
            },

            { path: 'products', name: 'products', component: ProductList, meta: { helpKey: 'products' } },
            {
                path: 'products/:publicId',
                name: 'products.show',
                component: ProductList,
                meta: { helpKey: 'products.show' },
            },
            {
                path: 'products/:publicId/edit',
                name: 'products.edit',
                component: ProductList,
                meta: { helpKey: 'products' },
            },

            {
                path: 'report-requests',
                name: 'report-requests',
                component: ReportRequestList,
                meta: { helpKey: 'report-requests' },
            },
            {
                path: 'report-requests/:publicId/status',
                name: 'report-requests.status',
                component: ReportRequestStatus,
                props: getPublicId,
                meta: { helpKey: 'report-requests.status' },
            },
            {
                path: 'report-requests/:publicId/process',
                name: 'report-requests.process',
                component: ReportRequestStatus,
                props: getPublicId,
                meta: { helpKey: 'report-requests.status' },
            },

            {
                path: 'integrations/create',
                name: 'integrations.create',
                component: IntegrationsCreate,
                meta: { helpKey: 'integrations.create' },
            },
            {
                path: 'integrations/:uuid/edit',
                name: 'integrations.edit',
                component: IntegrationsEdit,
                props: getUuid,
                meta: { helpKey: 'integrations.edit' },
            },

            {
                path: 'sales-channels',
                name: 'sales-channels.list',
                component: SalesChannelsList,
                meta: { helpKey: 'sales-channels.list' },
            },
            {
                path: 'sales-channels/:publicId',
                name: 'sales-channels.edit',
                component: SalesChannelsEdit,
                props: true,
                meta: { helpKey: 'sales-channels.edit' },
            },
            {
                path: 'settings',
                name: 'organisation.settings',
                component: OrganisationSettings,
                meta: { helpKey: 'organisation.settings' },
            },

            {
                path: 'store-orders',
                name: 'store-orders',
                component: StoreOrderList,
                meta: { helpKey: 'store-orders' },
            },
            {
                path: 'store-orders/:publicId',
                name: 'store-orders.show',
                component: StoreOrderList,
                meta: { helpKey: 'store-orders' },
            },
            {
                path: 'subscriptions',
                name: 'subscriptions',
                component: SubscriptionsList,
                meta: { helpKey: 'subscriptions' },
            },
            {
                path: 'marketing/coupons',
                name: 'marketing.coupons',
                component: CouponsList,
                meta: { helpKey: 'marketing-coupons' },
            },
            {
                path: 'marketing/coupons/:publicId',
                name: 'marketing.coupons.show',
                component: CouponsList,
                meta: { helpKey: 'marketing-coupons' },
            },
            {
                path: 'customers',
                name: 'customers',
                component: CustomersList,
                meta: { helpKey: 'customers' },
            },
            {
                path: 'customer-support',
                name: 'customer-support',
                component: CustomerSupportDashboard,
                meta: { helpKey: 'customer-support' },
            },
            { path: 'skus', name: 'skus', component: SkuList, meta: { helpKey: 'skus' } },
            {
                path: 'skus/mapping',
                name: 'sku-mapping',
                component: SkuMapping,
                meta: { helpKey: 'sku-mapping' },
            },
            {
                path: 'skus/:publicId',
                name: 'skus.show',
                component: SkuShow,
                props: getPublicId,
                meta: { helpKey: 'skus.show' },
            },
            {
                path: 'skus/:publicId/edit',
                name: 'skus.edit',
                component: SkuList,
                meta: { helpKey: 'skus' },
            },
            {
                path: 'skus/:publicId/mappings',
                name: 'skus.mappings',
                component: SkuList,
                meta: { helpKey: 'sku-mapping' },
            },
            {
                path: 'stock/analysis',
                name: 'stocks-analysis',
                component: StockAnalysis,
                meta: { helpKey: 'stocks-analysis' },
            },
            { path: 'stock', name: 'stocks', component: StockStatus, meta: { helpKey: 'stocks' } },
            { path: 'stock/health', name: 'health', component: HealthPage, meta: { helpKey: 'health' } },

            { path: 'suppliers/', name: 'suppliers', component: SuppliersList, meta: { helpKey: 'suppliers' } },
            {
                path: 'suppliers/create',
                name: 'suppliers.create',
                component: SuppliersList,
                meta: { helpKey: 'suppliers' },
            },
            {
                path: 'suppliers/:publicId',
                name: 'suppliers.show',
                component: SuppliersList,
                meta: { helpKey: 'suppliers' },
            },
            {
                path: 'suppliers/:publicId/edit',
                name: 'suppliers.edit',
                component: SuppliersList,
                meta: { helpKey: 'suppliers' },
            },
            {
                path: 'suppliers/:publicId/edit-products',
                name: 'suppliers.edit-products',
                redirect: (to: RouteLocationGeneric) => ({
                    name: 'suppliers.edit',
                    params: { publicId: to.params['publicId'] },
                    query: { tab: 'products' },
                }),
            },

            {
                path: 'warehouses',
                name: 'warehouses',
                component: WarehouseList,
                meta: { helpKey: 'warehouses' },
            },
            {
                path: 'warehouses/:publicId',
                name: 'warehouses.show',
                component: WarehouseList,
                meta: { helpKey: 'warehouses.show' },
            },
            {
                path: 'warehouses/:publicId/edit',
                name: 'warehouses.edit',
                component: WarehouseList,
                meta: { helpKey: 'warehouses.edit' },
            },

            {
                path: 'stores',
                name: 'stores',
                component: StoresList,
                meta: { helpKey: 'stores' },
            },

            {
                path: 'stores/:storePublicId/inventory-routing',
                name: 'stores.inventory-routing',
                component: InventoryRoutingPage,
                props: true,
                meta: { helpKey: 'stores.inventory-routing' },
            },

            // Accounting section
            {
                path: 'accounting/vat-reports',
                name: 'accounting.vat-reports',
                component: VATReports,
                meta: { helpKey: 'accounting.vat-reports' },
            },
            {
                path: 'accounting/cross-border-vat',
                name: 'accounting.cross-border-vat',
                component: CrossBorderVATReport,
                meta: { helpKey: 'accounting.cross-border-vat' },
            },
            {
                path: 'accounting/profit-loss',
                name: 'accounting.profit-loss',
                component: ProfitLossPage,
                meta: { helpKey: 'accounting.profit-loss' },
            },
        ],
    },
    {
        path: '/',
        component: LayoutBackend,
        meta: { requiresAuth: true },
        beforeEnter: (to: RouteLocationNormalized) => {
            // For user profile route, inject org context without changing URL
            if (to.name === 'user.profile') {
                to.params['organisation'] = localStorage.getItem('organisation') || 'unknown';
                return true;
            }

            if (!(to.fullPath === '/') || to.params['organisation']) {
                return true;
            }

            return {
                name: 'dashboard',
                params: {
                    organisation: localStorage.getItem('organisation'),
                    ...to.params,
                },
                query: to.query,
            };
        },
        children: [
            {
                path: '/amazon/connect',
                name: 'amazon.oauth.connect',
                component: AmazonOAuthForm,
                meta: { helpKey: 'amazon.oauth.connect' },
            },
            {
                path: '/user/profile',
                name: 'user.profile',
                component: UserProfile,
                meta: { helpKey: 'user.profile' },
            },
        ],
    },

    {
        path: '/organisation/:organisation/stock/analysis',
        component: LayoutBackendFull,
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'stocks-analysis',
                component: StockAnalysis,
                props: true,
                meta: { helpKey: 'stocks-analysis' },
            },
        ],
    },

    {
        path: '/organisation/:organisation/shipments',
        component: LayoutBackendFull,
        meta: { requiresAuth: true },
        children: [{ path: '', name: 'shipments', component: ShipmentsView, meta: { helpKey: 'shipments' } }],
    },

    {
        path: '/organisation/:organisation/transfers',
        component: LayoutBackendFull,
        meta: { requiresAuth: true },
        children: [
            {
                path: ':publicId?',
                name: 'transfers',
                component: WarehouseTransfersList,
                meta: { helpKey: 'transfers.list' },
            },
            {
                path: ':publicId/edit',
                name: 'transfers.edit',
                component: WarehouseTransfersList,
                meta: { helpKey: 'transfers.edit' },
            },
        ],
    },

    {
        path: '/organisation/:organisation/purchase-orders',
        component: LayoutBackendFull,
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'purchase-orders',
                component: PurchaseOrderList,
                meta: { helpKey: 'purchase-orders.list' },
            },
            {
                path: 'create',
                name: 'purchase-order.create',
                component: PurchaseOrderList,
                meta: { helpKey: 'purchase-orders.create' },
            },
            {
                path: ':publicId/edit',
                name: 'purchase-order.edit',
                component: PurchaseOrderList,
                meta: { helpKey: 'purchase-orders.edit' },
            },
        ],
    },

    {
        path: '/auth',
        component: LayoutSimple,
        children: [
            {
                path: 'login',
                component: Login,
                name: 'login',
                meta: { requiresAuth: false },
            },
            {
                path: 'register',
                component: Register,
                name: 'register',
                meta: { requiresAuth: false },
            },
            {
                path: 'forgot-password',
                component: ForgotPassword,
                name: 'forgot-password',
                meta: { requiresAuth: false },
            },
            {
                path: 'reset-password/:token',
                component: ResetPassword,
                name: 'reset-password',
                props: true,
                meta: { requiresAuth: false },
            },
            {
                path: 'password/create',
                component: CreatePassword,
                name: 'auth-password',
                meta: { requiresAuth: false },
            },
        ],
    },
];

function getPublicId(route: RouteLocationNormalized): { publicId: string } {
    const publicId = route.params['publicId'];
    return {
        publicId: typeof publicId === 'string' ? publicId : '',
    };
}

function getUuid(route: RouteLocationNormalized): { uuid: string } {
    const uuid = route.params['uuid'];
    return {
        uuid: typeof uuid === 'string' ? uuid : '',
    };
}

// Create Router
const router = createRouter({
    history: createWebHistory(),
    linkActiveClass: 'active',
    linkExactActiveClass: '',
    scrollBehavior() {
        return { left: 0, top: 0 };
    },
    routes,
});

// NProgress
NProgress.configure({ showSpinner: false });

router.beforeResolve((to) => {
    if (to.name) {
        NProgress.start();
    }
});

router.beforeEach(async (to) => {
    if (!to.meta['requiresAuth']) {
        console.debug("Doesn't need auth, skipping...");
        return true;
    }

    const authStore = useAuthStore();

    try {
        await authStore.getCurrentUser();
    } catch (error) {
        if (error instanceof LaravelError && error?.message && error?.message === 'Unauthenticated') {
            console.info('User is not logged in, redirecting to login...');
            return { name: 'login' };
        }
    }

    if (authStore.loggedIn) {
        console.info('User is logged in, continuing...');
        return true;
    }
    console.info('User is not logged in, redirecting to login...');
    return { name: 'login' };
});

// Lazy import promise cached once — avoids a circular dependency between router
// and stores while only paying the dynamic import cost on the first navigation.
let helpStoreModulePromise: ReturnType<typeof importHelpStore> | null = null;
let latestHelpNavPath: string | null = null;

function importHelpStore() {
    return import('@/stores/helpStore');
}

router.afterEach((to) => {
    NProgress.done();

    const navPath = to.fullPath;
    latestHelpNavPath = navPath;

    if (helpStoreModulePromise === null) {
        helpStoreModulePromise = importHelpStore();
    }

    helpStoreModulePromise
        .then(({ useHelpStore }) => {
            // Guard against out-of-order resolution: a later navigation may have
            // superseded this one while the dynamic import was in flight.
            if (latestHelpNavPath !== navPath) {
                return;
            }
            const helpKey = (to.meta as { helpKey?: string } | undefined)?.helpKey;
            useHelpStore().setRouteKey(helpKey ?? null);
        })
        .catch((error) => {
            console.warn('[help] failed to update route key', error);
            // Reset so a later navigation retries the dynamic import instead of
            // reusing the rejected promise.
            helpStoreModulePromise = null;
        });
});

router.onError((error, to) => {
    const isChunkError =
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Importing a module script failed') ||
        error.name === 'ChunkLoadError';

    if (isChunkError && to.fullPath.startsWith('/')) {
        // The navigation failed because old chunks are gone after a deployment.
        // Reload so the browser fetches the new entry‑point and manifest.
        window.location.href = to.fullPath;
    }
});

export default router;
